const GA_MEASUREMENT_ID = import.meta.env.VITE_GOOGLE_ANALYTICS_ID;

let isInitialized = false;

type EventValue = string | number | boolean | null | undefined;
type EventParams = Record<string, EventValue>;

function ensureDataLayer(): unknown[] {
  globalThis.dataLayer = globalThis.dataLayer || [];
  return globalThis.dataLayer;
}

function callNativeGtag(...args: unknown[]): boolean {
  const nativeGtag = (globalThis as unknown as Record<string, unknown>).gtag;
  if (typeof nativeGtag === 'function') {
    (nativeGtag as (...args: unknown[]) => void)(...args);
    return true;
  }
  return false;
}

function pushToDataLayer(...args: unknown[]): void {
  ensureDataLayer().push(args);
}

function gtag(...args: unknown[]): void {
  if (!callNativeGtag(...args)) {
    pushToDataLayer(...args);
  }
}

function isTrackingReady(): boolean {
  return isInitialized && !!GA_MEASUREMENT_ID;
}

/**
 * Initialize Google Analytics (only when cookie consent is granted).
 */
export function initializeAnalytics(): void {
  if (isInitialized || !GA_MEASUREMENT_ID) return;

  // Ensure opt-out flag is cleared if user re-accepts analytics.
  (globalThis as unknown as Record<string, unknown>)[`ga-disable-${GA_MEASUREMENT_ID}`] = false;

  const existingScript = document.querySelector<HTMLScriptElement>(
    `script[src="https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}"]`,
  );

  if (!existingScript) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);
  }

  gtag('js', new Date());
  gtag('config', GA_MEASUREMENT_ID, {
    // Manual pageview + section measurement for SPA clarity.
    send_page_view: false,
    anonymize_ip: true,
  });

  isInitialized = true;
}

/**
 * Disable Google Analytics (e.g., when consent is revoked).
 */
export function disableAnalytics(): void {
  if (!GA_MEASUREMENT_ID) return;
  (globalThis as unknown as Record<string, unknown>)[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
  isInitialized = false;
}

/**
 * Track a page view for SPA navigation/events.
 */
export function trackPageView(path: string, title: string): void {
  if (!isTrackingReady()) return;
  gtag('event', 'page_view', {
    page_path: path,
    page_title: title,
    page_location: globalThis.location.href,
    language: document.documentElement.lang || undefined,
  });
}

/**
 * Generic custom/recommended event tracking wrapper.
 */
export function trackEvent(name: string, params: EventParams = {}): void {
  if (!isTrackingReady()) return;
  gtag('event', name, params);
}

/**
 * Section impression event for single-page analytics.
 */
export function trackSectionView(sectionId: string): void {
  trackEvent('view_section', {
    section_id: sectionId,
    content_type: 'section',
  });
}

declare global {
  // dataLayer is attached on global object by gtag bootstrap.
  var dataLayer: unknown[] | undefined;
}
